import React, { useEffect } from "react";
import { LegalLayout, LegalH2, LegalP, LegalUl } from "../components/LegalLayout";

const Terms = () => {
    useEffect(() => {
        document.title = "Terms of Service — Voro";
        window.scrollTo(0, 0);
    }, []);

    return (
        <LegalLayout
            testId="page-terms"
            title="Terms of Service"
            updated="11 June 2026"
        >
            <LegalP>
                Welcome to Voro. These Terms of Service (<strong>"Terms"</strong>) govern your
                use of{" "}
                <a href="https://voro.au" className="text-violet-300 hover:underline">
                    voro.au
                </a>{" "}
                and the Voro mobile application (together, the <strong>"Service"</strong>),
                operated by Voro Pty Ltd (<strong>"Voro"</strong>, <strong>"we"</strong>,{" "}
                <strong>"us"</strong>). By accessing the Service or joining our waitlist,
                you agree to these Terms.
            </LegalP>

            <LegalH2>1. Eligibility</LegalH2>
            <LegalP>
                You must be at least 16 years old and able to form a legally binding contract
                under Australian law to use the Service. By using Voro, you represent that you
                meet these requirements.
            </LegalP>

            <LegalH2>2. The waitlist</LegalH2>
            <LegalP>
                Voro is currently in pre-launch. Joining the waitlist places you on our list
                of prospective users — it does not guarantee access, pricing, or features.
                The final product may differ from what is described on voro.au.
            </LegalP>

            <LegalH2>3. Acceptable use</LegalH2>
            <LegalP>You agree not to:</LegalP>
            <LegalUl>
                <li>Use the Service for any unlawful, harmful, or fraudulent purpose;</li>
                <li>
                    Submit information that is false, misleading, defamatory, or that infringes
                    on someone else's rights;
                </li>
                <li>
                    Attempt to interfere with, reverse-engineer, or gain unauthorised access to
                    any part of the Service;
                </li>
                <li>Scrape, copy, or redistribute content from voro.au without permission; or</li>
                <li>Use the Service to harass, intimidate, or harm any other person.</li>
            </LegalUl>

            <LegalH2>4. Future product updates</LegalH2>
            <LegalP>
                Voro is being actively built. Features, pricing, availability, and access
                rules may change. We may release new versions, deprecate features, or modify
                the Service at our discretion. Where changes materially affect you, we'll do
                our best to give you reasonable notice.
            </LegalP>

            <LegalH2>5. Intellectual property</LegalH2>
            <LegalP>
                All content on voro.au — including the Voro name, logo, branding, copy,
                graphics, screenshots, design, and code — is owned by Voro Pty Ltd or its
                licensors and is protected by Australian and international intellectual
                property laws. You may not reproduce, distribute, or create derivative works
                from any part of the Service without our prior written consent.
            </LegalP>
            <LegalP>
                Voice, names, and likenesses appearing in app mockups and marketing materials
                are illustrative and do not represent real users without consent.
            </LegalP>

            <LegalH2>6. Your content</LegalH2>
            <LegalP>
                When the full app launches, you'll retain ownership of any content you submit
                (workout logs, profile information, posts, etc.). By submitting content, you
                grant Voro a non-exclusive, worldwide, royalty-free licence to host,
                reproduce, and display that content solely to operate and improve the
                Service.
            </LegalP>

            <LegalH2>7. Privacy</LegalH2>
            <LegalP>
                Our handling of personal information is described in our{" "}
                <a href="/privacy" className="text-violet-300 hover:underline">
                    Privacy Policy
                </a>
                , which forms part of these Terms.
            </LegalP>

            <LegalH2>8. Disclaimer</LegalH2>
            <LegalP>
                The Service is provided "as is" and "as available". Voro is a fitness and
                community platform — it is not a substitute for professional medical, health,
                or training advice. Always consult a qualified professional before starting
                or changing a fitness regimen. You participate in any workout, challenge, or
                meet-up at your own risk.
            </LegalP>
            <LegalP>
                To the maximum extent permitted by law, we exclude all warranties, conditions,
                and representations not expressly set out in these Terms.
            </LegalP>

            <LegalH2>9. Limitation of liability</LegalH2>
            <LegalP>
                Nothing in these Terms excludes, restricts, or modifies any rights you have
                under the Australian Consumer Law that cannot be excluded. Subject to those
                non-excludable rights, to the maximum extent permitted by law:
            </LegalP>
            <LegalUl>
                <li>
                    Voro is not liable for any indirect, consequential, special, or punitive
                    loss arising out of or in connection with your use of the Service; and
                </li>
                <li>
                    Our total aggregate liability for any claim relating to the Service is
                    limited to AUD $100.
                </li>
            </LegalUl>

            <LegalH2>10. Termination</LegalH2>
            <LegalP>
                We may suspend or terminate your access to the Service at any time if you
                breach these Terms or if we discontinue any part of the Service. You can stop
                using Voro at any time and request removal from our waitlist by emailing{" "}
                <a
                    href="mailto:hello@voro.au"
                    className="text-violet-300 hover:underline"
                >
                    hello@voro.au
                </a>
                .
            </LegalP>

            <LegalH2>11. Governing law</LegalH2>
            <LegalP>
                These Terms are governed by the laws of Western Australia. You and Voro agree
                to submit to the non-exclusive jurisdiction of the courts of Western
                Australia for any disputes arising from these Terms or the Service.
            </LegalP>

            <LegalH2>12. Changes to these Terms</LegalH2>
            <LegalP>
                We may update these Terms from time to time. The "Last updated" date at the
                top of this page will always reflect the current version. Continued use of the
                Service after changes are posted constitutes acceptance of the updated Terms.
            </LegalP>

            <LegalH2>13. Contact</LegalH2>
            <LegalP>
                Questions about these Terms? Email{" "}
                <a
                    href="mailto:hello@voro.au"
                    className="text-violet-300 hover:underline"
                >
                    hello@voro.au
                </a>
                .
            </LegalP>
        </LegalLayout>
    );
};

export default Terms;
